import { useCallback, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Device } from '@/api';
import { useOpenApi } from '@/common/providers/open-api-provider';
import { getFallbackErrorKey, isApiError } from '@/common/util';
import { useDevicesTranslation } from '../hooks';
import { FETCH_DEVICE_QUERY_KEY } from './devices-query-keys';
import { addOrUpdateDeviceCache } from './devices-cache';

export type FetchDeviceResult = {
    status: 'error' | 'success' | 'pending';
    device: Device | undefined;
    /** Why the read failed, ready to show. Empty while it has not. */
    error: string;
    /** False when retrying cannot help — the device is gone, or refused. */
    canRetry: boolean;
    refetch: () => void;
};

export function useFetchDevice(deviceId: string): FetchDeviceResult {
    const { t } = useDevicesTranslation();

    // The client is swapped out on token refresh, so never capture it: read
    // `openApiRef.current` at call time or you keep using a dead token.
    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });

    const queryClient = useQueryClient();

    // One place that turns a failure into a message, so every screen reading
    // this device says the same thing. Every error `GET /v3/devices/{deviceId}`
    // declares gets a branch.
    const describeError = useCallback(
        (error: unknown): { message: string; canRetry: boolean } => {
            if (isApiError(error, 'DEVICE_NOT_FOUND')) {
                return {
                    message: t('shared.errors.device_load_not_found'),
                    canRetry: false
                };
            }
            if (isApiError(error, 'NO_PERMISSION')) {
                return {
                    message: t('shared.errors.device_load_no_permission'),
                    canRetry: false
                };
            }
            if (isApiError(error, 'BAD_REQUEST')) {
                return { message: t('shared.errors.generic'), canRetry: false };
            }

            // Anything else — a dropped connection, a 500 — may well work on a
            // second press.
            return { message: t(getFallbackErrorKey(error)), canRetry: true };
        },
        [t]
    );

    const {
        status,
        data: device,
        refetch,
        error
    } = useQuery({
        queryKey: [FETCH_DEVICE_QUERY_KEY, deviceId],
        queryFn: () => {
            return openApiRef.current.devices
                .getDevice(deviceId)
                .then((response) => {
                    const device = response.data.data.device;
                    addOrUpdateDeviceCache(queryClient, device);
                    return device;
                });
        },
        // While a device command (reboot/power-off/config change) is pending,
        // poll every 10s so the UI reflects the device's reported state.
        refetchInterval: (query) => {
            const hasPendingCommand = query.state.data?.commands?.some(
                (command) => command.status === 'pending'
            );
            return hasPendingCommand ? 10000 : false;
        }
    });

    const described = error ? describeError(error) : null;

    return {
        status,
        device,
        error: described?.message ?? '',
        canRetry: described?.canRetry ?? true,
        refetch: () => {
            void refetch();
        }
    };
}
